/* eslint-disable no-template-curly-in-string */

/**
  * @class mmodAppBar
  * @description
  * Application Bar is integrated part of the user interface
  * used for switching between the modules.
  * It can be displayed either horizontally (usually in mPatrol)
  * or vertically (usually in mOffice).
  * Please note that for mPatrol on mobile phones Application Logo
  * with Application Wall is used due to constrained size (see next chapters).
  * Application Bar displays list of available modules and highlights the active one.
  * It can also display status icons, informing user on connection to server and peering
  * and status of GPS tracking and radar.
  * In horizontal mode it also displays current logged callsign, operational status and time.
  * In this case user can open a modal window to change operational status,
  * language and perform logout, shutdown or change password.
  * Module is implemented without workflow, as a pure module named mmodAppBar.
  * Module listens on various messages to update its appearance accordingly:
  * - mmStatus - to update list of modules
  * - mmWindowFocused - to update currently active module
  * - mmUserLogged - used to update callsign of user
  * - mmUsersStatus - used to update operational status of user
  * - mmServerConnected/mmServerDisconnected - to update server connection icon
  * - mmPeeringConnected/mmPeeringDisconnected - to update peering connection icon
  * - mmDirectConnected/mmDirectDisconnected - to update radar connection icon
  * - mmGps - to update GPS icon
  */

import mmodModule from 'mmodModule'
const moment = require('moment')

export default class mmodAppBar extends mmodModule {
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates an instance of the class.
  * @param {string} [id] - ID under which the object will listen for messages
  * @param {string} [cfg] - object initial configuration
  * @param {string} [cfg_schema] - additional schema properties from derived classes
  * @param {string} [dependencies] - additional dependencies from derived classes
  */
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union([/* put module specific dependencies here */], dependencies)
    cfg_schema = _.extend({
      init: {
        type: 'object',
        default: { type: 'dock', side: 'bottom', height: 64, order: 2 },
        description: 'Initial window setup; same structure as required by `mmWindowSet`',
      },
      dashboard: {
        type: 'object',
        default: { type: 'dock', side: 'bottom', height: 64, order: 2 },
        description: 'Initial window setup; same structure as required by `mmWindowSet`',
      },
      desktop: {
        type: 'object',
        default: { type: 'dock', side: 'left', width: 80, order: 2 },
        description: 'Window setup for desktop; same structure as required by `mmWindowSet`',
      },
      modules: {
        description: 'list of user modules in application bar',
        type: 'array',
        default: ['mmodMap', 'mmodPlayer', 'mmodIncidents', 'mmodInstructions', 'mmodAlpr', 'mmodScreenings', 'mmodFines', 'mmodUnits', 'mmodChats', 'mmodWiki'],
      },
      admin_modules: {
        description: 'list of admin modules in application bar',
        type: 'array',
        default: [],
      },
      admin_password: {
        description: 'password for admin mode',
        type: 'string',
        default: 'nev123mosy',
      },
      mode: {
        description: 'mode on app start',
        type: 'string',
        enum: ['user', 'admin'],
        default: 'user',
      },
      module_on_login: {
        description: 'initial app module after log in; either name of module or number as index of visible module',
        type: 'string',
      },
      statusclick: {
        description: 'what to do when user clicks on status area',
        type: 'string',
        enum: ['toolbar', 'appwall', 'status', 'logout', 'none'],
        default: 'appwall',
      },
      menu_items: {
        description: 'additional items in status menu',
        type: 'array',
        items: { type: 'object' },
        default: [
          { key: 'logout', label: 'login.logout', iconleft: 'sys.logout' },
          { key: 'printer_test', label: 'settings.printer_test', iconleft: 'sys.printer' },
          { key: 'system', label: 'settings.system', iconleft: 'settings' },
        ],
      },
      dtformat: {
        description: 'format for date time',
        type: 'string',
        default: 'HH:mm:ss',
      },
      gps_valid_time: {
        description: 'validity of last GPS position (used to determine if GPS is on or off) - in ms',
        type: 'number',
        default: 10000,
      },
      password_minlength: {
        description: 'minimum length for new password',
        default: 8,
        type: 'number',
      },
      allow_shutdown: {
        description: 'allow shutdown button',
        default: false,
        type: 'boolean',
      },
      languages: {
        description: 'list of displayed languages',
        type: 'array',
        items: { type: 'string' },
        default: [],
      },
      indicators: {
        description: 'which indicators to display',
        type: 'array',
        items: { type: 'string', enum: ['online', 'peering', 'gps', 'doppler'] },
        default: ['online', 'peering', 'gps', 'doppler'],
      },
      password_lowercase: { description: 'whether lowercase character is required in new password', type: 'boolean', default: true },
      password_uppercase: { description: 'whether uppercase character is required in new password', type: 'boolean', default: true },
      password_letter: { description: 'whether latin character is required in new password', type: 'boolean', default: false },
      password_digit: { description: 'whether digit is required in new password', type: 'boolean', default: true },
      password_letterdigit: { description: 'whether latin character or digit is required in new password', type: 'boolean', default: false },
      password_symbol: { description: 'whether special symbol character is required in new password', type: 'boolean', default: false },
      password_symboldigit: { description: 'whether special symbol character or digit is required in new password', type: 'boolean', default: false },
    }, cfg_schema)
    if(require('msysFS').device()==='phone') cfg.disabled = true
    super(id, cfg, cfg_schema, dependencies)
    this.module_statuses = {}
    this.status = 'boffline'
    this.login = ''
    this.callsign = ''
  } // constructor

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * initialization of GUI components
  */
  initWindow() {
    this.loadForm(require('./mmodAppBar.json'))
    this._elAppBar = this.el.find('@appbar')
    this._elAppBar.items = this.config.modules
    this.hideHiddenModules(this._elAppBar)
    this._elStatus = this.el.find('@statusbox')
    this._elStatus.onclick = ()=>{
      this.publish('#ME', 'mmButtonClicked', { holder: 'appbar', button: 'statusbox' })
    }
    this.el.forAll('mele-image@logo', elLogo=>{
      elLogo.onclick = ()=>{
        this.publish('#ME', 'mmButtonClicked', { holder: 'appbar', button: 'logo' })
      }
    })
    this.elIndicators = this.el.find('@indicators')

    if(this.config.indicators?.length>0) {
      const elements = {
        online: {
          tag: 'mele-input',
          type: 'check',
          class: 'small',
          key: 'online',
          prefix: 'sys.server.',
          readonly: true,
        },
        peering: {
          tag: 'mele-input',
          type: 'check',
          class: 'small',
          key: 'peering',
          prefix: 'sys.peers.',
          readonly: true,
        },
        gps: {
          tag: 'mele-input',
          type: 'check',
          class: 'small',
          key: 'gps',
          prefix: 'sys.gps.',
          readonly: true,
        },
        doppler: {
          tag: 'mele-input',
          type: 'icon',
          class: 'small',
          key: 'doppler',
          prefix: 'sys.doppler.',
          value: 0,
          readonly: true,
        },

      }

      for(const ind of this.config.indicators) {
        if(elements[ind]) {
          this.elIndicators.appendChildren([elements[ind]])
        }
      }
    }

    const elDT = this._elStatus.find('@datetime')
    const elGPS = this.elGPS = this.el.find('@indicators @gps')
    this.elDoppler = this.el.find('@indicators @doppler')

    this.timer = setInterval(()=>{
      elDT.label = moment().format(this.config.dtformat)
      elGPS.value = Date.now() - this.last_gps_update < this.config.gps_valid_time ? 1 : 0
    }, 100)

    // click on indicators will switch app bar mode from user modules to admin modules and vice versa
    this.el.find('@indicators').onclick = ()=>{
      this.publish('#ME', 'mmButtonClicked', { holder: 'appbar', button: 'indicators' })
    }

    this.mmStart()
    this.subscribe('#WM')
    this.subscribe('#GPS')
    this.subscribe('#DOPPLER')
    this.subscribe('#DEVICES')
    this.subscribe('#KEYPRESS')

    this.publish('#ALLWITHME', 'mmMode', { mode: this.config.mode || 'user' })

    this.count = 0
    this.count_timer = null
  } // initWindow

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * KeyDown event
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.key] - pressed key value
  * @param {integer} [params.keyCode] - keycode value
  * @param {boolean} [params.repeat]
  * @param {boolean} [params.shift]
  * @param {boolean} [params.ctrl]
  * @param {boolean} [params.alt]
  * @param {boolean} [params.meta]
  */
  mmKeypress(sender, params) {
    if (params.ctrl && parseInt(params.keyCode) === 99) { // Numpad3 keyCode is 99
      // Increment the counter
      this.count++

      // Clear any existing timeout
      if (this.timer) {
        clearTimeout(this.timer)
      }

      // Set a timeout to reset the counter if no further keypress within 300ms
      this.timer = setTimeout(() => {
        this.count = 0
      }, 200)

      // Check if Numpad1 was pressed 3 times
      if (this.count === 3) {
        this.publish(':msvrApplications', 'mmPanic', {})
        this.mmListSelected(sender, { holder: 'status', selected: 'ronscene' })
        this.count = 0 // Reset the counter after publishing
        clearTimeout(this.timer) // Clear the timeout after successful action
      }
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Request to exit the listener
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {boolean} [params.delete] - if true, will be deleted from list of listeners
  * @param {string} [params.error] - reason for exit
  */
  mmExit(sender, params) {
    if(this.timer) {
      clearInterval(this.timer)
      delete this.timer
    }
    super.mmExit(sender, params)
  } // mmExit

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sends by Window Manager when top full-screen window on any screen changes. Sends directly to that window and to #WM queue.
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.id]
  * @param {integer} [params.screen]
  */
  mmWindowFocused(sender, params) {
    this.el.forAll('@appbar', el=>{
      el.selected=[params.id]
    })
  } // mmWindowFocused

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * information on changed user authorization; user_id is empty if not logged
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - unique application ID
  * @param {integer} [params.user_id] - user's unique ID
  * @param {string} [params.login] - user's login name
  * @param {string} [params.callsign] - user's callsign
  * @param {object} [params.roles] - roles assigned to user
  * @param {string} [params.privatekey] - user's private key encrypted by his password
  * @param {string} [params.error] - error message if login failed
  */
  mmUserLogged(sender, params) {
    if(params.user_id>0 && _.natural(params.user_id)!==_.natural(this.user_id)) {
      this.focusAppAfterLogin()
    }
    super.mmUserLogged(sender, params)
    this._elStatus.find('@callsign').label = _.toString(params.callsign) || _.toString(params.login) || 'No callsign'
    this._elStatus.find('@login').label = _.toString(params.login) || 'Not logged'
  } // mmUserLogged

  focusAppAfterLogin() {
    let module_on_login = _.toString(this.config.module_on_login)
    if(module_on_login>0) {
      // opening n-th is a bit tricky, as we know visible items only after we were displayed, that could take some time
      this._elAppBar.setProperty('selected', ['mmodEmpty'], true)
      this.whenWeAreDisplayed(20, 100, ()=>{
        // wait until we are displayed
        const visible_items = this._elAppBar.findAll('mele-item[key]').filter(el=>el.offsetParent) // hack how to know which items are displayed
        if(visible_items.length>0) {
          module_on_login = _.min([module_on_login-1, visible_items.length-1])
          module_on_login = visible_items[module_on_login].key
          if(module_on_login.length>0) {
            this._elAppBar.setProperty('selected', [module_on_login], true)
          }
        }
      })
    }else if(module_on_login.length>0) {
      // this is easy, module name was directly specified
      this._elAppBar.setProperty('selected', ['mmodEmpty'], true)
      this._elAppBar.setProperty('selected', [module_on_login], true)
    }
  } // focusAppAfterLogin

  whenWeAreDisplayed(retries, delay, callback, callback_error) {
    const ready = this._elAppBar.findAll('mele-item').filter(el=>el.offsetParent).length>0
    if(ready) return callback()
    if(retries>0) {
      setTimeout(this.whenWeAreDisplayed.bind(this, retries-1, delay, callback, callback_error), delay)
    }else{
      callback_error && callback_error()
    }
  } // whenWeAreDisplayed

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * notification on changed user status
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - unique application ID
  * @param {string} params.status - new status
  * @param {string} [params.info] - additional info about status change
  */
  mmUserStatus(sender, params) {
    super.mmUserStatus(sender, params)
    this._elStatus.find('@status').label = 'unit_status.'+params.status
    this._elStatus.setAttribute('color',
      /^g/.test(params.status) ? 'green' :
        /^o/.test(params.status) ? 'orange' :
          /^r/.test(params.status) ? 'red' :
            'gray',
    )
  } // mmUserStatus

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Request to change application behavior
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.behavior] - desired behavior
  */
  mmSetBehavior(sender, params) {
    const behavior = super.mmSetBehavior(sender, params)
    const setup =
      behavior==='tablet' || behavior==='phone' ? this.config.init :
        behavior==='dashboard' ? this.config.dashboard :
          behavior==='desktop' ? this.config.desktop :
            { type: 'none' }
    this.publish('msysWM', 'mmWindowSet', setup)
    if(setup?.side==='left' || setup?.side==='right') {
      this._elAppBar.horizontal = false
      this._elAppBar.direction = 'column'
    }else {
      this._elAppBar.horizontal = true
      this._elAppBar.direction = 'row'
    }
  } // mmSetBehavior

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Request to change application color theme
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.theme] - desired color theme
  */
  mmSetTheme(sender, params) {
    this.theme = params.theme
    this.el.forAll('mele-item[key=theme]', el=>{
      el.label = 'theme.'+params.theme
      el.iconleft = 'sys.theme.'+params.theme
    })
    super.mmSetTheme(sender, params)
  } // mmSetTheme

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sends by &lt;mele-button> to parent module when a button is clicked
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.holder] - key attribute of button's parent container
  * @param {string} params.button - key attribute of the button
  * @param {integer} [params.count] - number of times button was clicked
  * @param {boolean} [params.long] - shows if press was long or not
  */
  async mmButtonClicked(sender, params) {
    const { button } = params
    if(button === 'logo') {
      const msg = `
        <b>${MOSY_APP}</b><br/>
        appid: ${_.toString(this.app_id)}<br/>
        version: ${MOSY_APP_WITH_VERSION}<br/>
        app ip: ${_.toString(this.my_ip)}<br/>
        server ip: ${_.toString(this.server_url)}<br/>
        user: ${_.toString(this.login)} / ${_.toString(this.user_id)}<br/>
        callsign: ${_.toString(this.callsign)}<br/>
      `
      this.modalInfo(msg)
    }else if(button === 'indicators') {
      if(this.config.admin_modules.length === 0) return
      if(this.mode==='admin') {
        // turn off admin mode
        this.publish('#ALLWITHME', 'mmMode', { mode: this.mode==='user'?'admin':'user' })
      }else {
        // turn on admin mode
        const admin_password = String(this.config.admin_password??'')
        if(admin_password.length>0) {
          const password = await this.modalPrompt([
            { tag: 'mele-text', label: 'Enter admin password' },
            { tag: 'mele-input', type: 'password', key: 'password' },
          ])
          if(password?.password !== admin_password) {
            return
          }
        }
        this.publish('#ALLWITHME', 'mmMode', { mode: this.mode==='user'?'admin':'user' })
      }
    }else if(button === 'statusbox') {
      if(this.config.statusclick==='appwall') {
        this.publish('mmodAppWall', 'mmOpen')
      }else if(this.config.statusclick==='toolbar') {
        this.modalOpen({
          class: 'buttons',
          outclick: ':modal.cancel',
          position: 'below',
          items: [{
            tag: 'mele-list',
            key: 'toolbar',
            design: 'appbar',
            horizontal: true,
            direction: 'row',
            items: [
              { key: 'theme', label: 'day', iconleft: 'sys.theme.day' },
              { key: 'status', label: 'Change Status' },
            ],
          }],
        })
      }else if(this.config.statusclick==='status') {
        this.openModalMenu()
      }else if(this.config.statusclick==='logout') {
        const yes = await this.modalConfirm('Are you sure?', 'login.logout')
        if(yes) {
          this.logoutUser()
        }
      }else if(this.config.statusclick==='none') {
        // Status button was configured to do nothing.
      }
    }
  } // mmButtonClicked

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Displays vertical menu for change of user status and few special actions
  */
  openModalMenu() {
    const elModal = this.modalOpen({
      key: 'appbar.status',
      class: 'list',
      outclick: ':modal.cancel',
      position: 'below',
      items: [{
        tag: 'mele-list',
        key: 'status',
        prefix: 'unit_status.',
        label: '${prefix}${key}',
        color: 'color:${prefix}${key}',
        auto: true,
      }],
    })
    const elStatus = elModal.find('@status')
    if(Array.isArray(this.config.languages) && this.config.languages.length>1) {
      elStatus.prependItems([
        {
          key: 'language',
          tag: 'mele-input',
          type: 'group',
          label: '',
          prefix: '',
          itemlabel: '${key}',
          options: this.config.languages,
          value: this.el.language,
          eventValueChanged: (self, value)=>{
            this.publish('#ALLWITHME', 'mmSetLanguage', { lang: value })
          },
        },
      ])
    }
    const menu_items = Array.isArray(this.config.menu_items) ? this.config.menu_items : []

    elStatus.prependItems(menu_items)
  } // openModalMenu

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Displays vertical menu for some system actions
  */
  openSystemMenu() {
    const elModal = this.modalOpen({
      key: 'appbar.system',
      class: 'list',
      outclick: ':modal.cancel',
      position: 'below',
      items: [{
        tag: 'mele-list',
        key: 'system',
        items: [
        ],
      }],
    })
    const elStatus = elModal.find('@system')
    if(this.config.allow_shutdown) {
      this._win = global.nw ? global.nw.Window.get() : null
      elStatus.prependItems([
        { key: 'reboot', label: 'login.reboot', iconleft: 'sys.logout' },
        { key: 'exit', label: 'login.exit', iconleft: 'sys.logout' },
        { key: 'kiosk', label: 'login.kiosk', iconleft: 'device.desktop' },
      ])
    }
  } // openSystemMenu

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sends by <mele-list> when selected item change
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.holder] - key attribute of the list
  * @param {string} params.selected - key attribute of the selected items separated by ∫
  * @param {string} [params.old] - key attribute of previously selected item
  * @param {boolean} [params.interactive] - indicates if this was user activity
  */
  mmListSelected(sender, params) {
    if(params.holder==='appbar') {
      if(params.selected==='theme') {
        this.el.find('mele-list[key=appbar]').selected = params.old
        this.publish('#ALLWITHME', 'mmSetTheme', { theme: this.theme==='day'?'night':'day' })
      }else if(params.selected==='mmodMarker') {
        this.el.find('mele-list[key=appbar]').selected = params.old
        this.publish('mmodMarker', 'mmButtonClicked', { button: 'marker' })
      }else if(params.selected==='mmodReconnect') {
        this.publish('msysPeeringClient', 'mmPeeringDiscover')
      }else if(params.interactive) {
        this.publish('msysWM', 'mmWindowFocus', { id: params.selected })
        this.publish('#ME', 'mmClose')
      }
    }else if(params.holder==='status') {
      if(params.selected==='printer_test') {
        this.publish('#PRINT', 'mmPrint', { test: true })
      } else if(params.selected==='logout') {
        this.logoutUser()
      }else if(params.selected==='changepwd') {
        this.modalChangePassword()
      }else if(params.selected==='exit') {
        this.publish('#ALLWITHME', 'mmExit', {})
      }else if(params.selected==='kiosk') {
        if(this._win) {
          this._win.toggleKioskMode()
        } else {
          this.warn('NW.js not available')
        }
      }else if(params.selected==='system') {
        this.openSystemMenu()
      }else{
        this.publish('msysUser', 'mmUserStatus', { status: params.selected })
      }
      this.modalClose('appbar.status')
    }else if(params.holder==='system') {
      if(params.selected==='exit') {
        this.publish('#ALLWITHME', 'mmExit', {})
      }else if(params.selected==='exit') {
        this.publish('#ALLWITHME', 'mmExit', {})
      }else if(params.selected==='reboot') {
        this.modalConfirm('login.reboot_confirm', 'login.reboot', ()=>{
          this.publish('@msvcStatusLinux', 'mmReboot', {})
        })
      }else if(params.selected==='kiosk') {
        if(this._win) {
          this._win.toggleKioskMode()
        } else {
          this.warn('NW.js not available')
        }
      }else{
        this.publish('msysUser', 'mmUserStatus', { status: params.selected })
      }
      this.modalClose('appbar.system')
    }
    // Generic notification removal for any module
    if (params.holder === 'appbar' && params.selected) {
      this.removeModuleNotification(params.selected)
    }
  } // mmListSelected

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Opens modal dialog for password change
  */
  modalChangePassword() {
    const modal = this.modalOpen(require('./mmodAppBar_changepwd.json'))
    const pwd0 = modal.find('@password')
    const pwd1 = modal.find('@newpassword')
    const pwd2 = modal.find('@newpassword2')
    const cfg = this.config
    pwd1.validateFormat = function() {
      pwd2.validate() // on change of pwd1, check also pwd2
      const value = _.toString(this.value)
      const valid =
      value.length>=cfg.password_minlength &&
      (!cfg.password_lowercase || /[a-z]/.test(value)) &&
      (!cfg.password_uppercase || /[A-Z]/.test(value)) &&
      (!cfg.password_digit || /[0-9]/.test(value)) &&
      (!cfg.password_letter || /[a-zA-Z]/.test(value)) &&
      (!cfg.password_letterdigit || /[a-zA-Z0-9]/.test(value)) &&
      (!cfg.password_symbol || new RegExp('['+'!@#$%^&*()_+{}:"|<>?~,./;\'\\[]-='.replace(/(.)/g, '\\$1')+']').test(value)) &&
      (!cfg.password_symboldigit || new RegExp('[0-9'+'!@#$%^&*()_+{}:"|<>?~,./;\'\\[]-='.replace(/(.)/g, '\\$1')+']').test(value))

      if(!valid) {
        this.formaterror = 'Your password does not meet security criteria'
        return false
      }

      if(_.toString(value) === _.toString(pwd0.value)) {
        this.formaterror = 'New password cannot be the same as old password'
        return false
      }

      return true
    }
    pwd2.validateFormat = ()=>{
      return pwd1.value===pwd2.value
    }
  } // modalChangePassword

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sent by &lt;mele-container> or derived class to parent module when form is submitted
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} params.holder - key attribute of the form
  * @param {string} [params.button] - key attribute of the button
  * @param {string} [params.datakey] - key attribute of the content
  * @param {object} params.values - hash of key:value of contained editable inputs
  * @param {boolean} [params.invalid] - true if any of form's inputs is invalid
  * @param {boolean} [params.conflict] - true if any of form's inputs has conflict
  */
  async mmFormSubmit(sender, params) {
    if(params.holder==='changepwd') {
      this.modalClose('changepwd')
      this.modalWait('{{Please wait}}...', 'Change password')
      try{
        await this.request('msysUser', 'mmUserPasswordChange', _.pick(params.values, ['password', 'newpassword']), { resolve: 'mmSuccess', timeout: 20000 })
        this.modalInfo('Password has been changed', 'Change password')
      }catch(e) {
        this.modalError('Error changing password', 'Change password')
      }
    }
  } // mmFormSubmit

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * msysDomainClient informs all listeners that connection to mServer is established
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - app_id of the server
  * @param {string} params.url - URL of connected server
  * @param {string} [params.ident] - Identity string of server
  * @param {string} [params.client_ip] - IP of the client as recognized by the server
  */
  mmServerConnected(sender, params) {
    this.my_ip = params.client_ip
    this.server_url = params.url
    this.el.forAll('mele-input@online', el=>{ el.value = 1 })
  } // mmServerConnected

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * msysDomainClient informs all listeners that connection to mServer has dropped
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.reason] - reason for close
  * @param {string} params.url - URL of disconnected server
  */
  mmServerDisconnected(sender, params) {
    this.my_ip = ''
    this.el.forAll('mele-input@online', el=>{ el.value = 0 })
  } // mmServerDisconnected

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * information on change of location
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.dev] - ID of device producing the GPS info
  * @param {string} params.gps - geohash of GPS position
  * @param {number} [params.speed] - speed in km/h
  * @param {number} [params.heading] - movement heading in degrees
  * @param {integer} [params.satellites] - number of satellites
  * @param {integer} [params.gps_dt] - GPS reported time in microseconds
  * @param {integer} [params.fix] - true if exists on sattelite
  */
  mmGps(sender, params) {
    if(params.gps) { this.last_gps_update = Date.now() }
  } // mmGps

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * msysPeeringClient informs all listeners that connection to peering is established
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - unique application ID
  * @param {string} [params.url] - URL of connected peering
  * @param {string} [params.ident] - Identity string of server
  * @param {string} [params.client_ip] - IP of the client as recognized by the peering
  */
  mmPeeringConnected(sender, params) {
    const el = this.el.find('@peering')
    if(el) el.value = 1
  } // mmPeeringConnected

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * msysPeeringClient informs all listeners that connection to peering has dropped
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.reason] - reason for close
  * @param {string} [params.url] - URL of disconnected peering
  */
  mmPeeringDisconnected(sender, params) {
    const el = this.el.find('@peering')
    if(el) el.value = 0

    if(this.elDoppler) this.elDoppler.value = 0
    if(this.elGPGS) this.elGPS.value = 0
  } // mmPeeringDisconnected

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Status of the listener
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.status]
  */
  mmStatus(sender, params) {
    this.module_statuses[sender.listener_id] = params.status
    this.hideHiddenModules(this._elAppBar)
    const elItem = this._elAppBar.getItemElement(sender.listener_id)
    if(elItem) elItem.keyChanged()
    setTimeout(()=>{ this.hideHiddenModules(this._elAppBar) }, 1000)
    setTimeout(()=>{ this.hideHiddenModules(this._elAppBar) }, 5000)
    setTimeout(()=>{ this.hideHiddenModules(this._elAppBar) }, 10000)
  } // mmStatus

  hideHiddenModules(elList) {
    elList.getItemElements().forEach(el=>{
      const status = this.module_statuses[el.key]
      el.setAttribute('status', status||'ok')
    })
    let elLastGroup = null; let elLastGroupHidden = true
    const win = this.el.ownerDocument.defaultView
    elList.getItemElements().forEach(el=>{
      if(el.key) {
        if(elLastGroupHidden) {
          const hidden = win.getComputedStyle(el).display==='none'
          if(!hidden) elLastGroupHidden=false
        }
      }else{
        if(elLastGroup) elLastGroup.setAttribute('status', elLastGroupHidden?'hidden':'')
        elLastGroup = el
        elLastGroupHidden = true
      }
    })
    if(elLastGroup) elLastGroup.setAttribute('status', elLastGroupHidden?'hidden':'')

    const elMarker = elList.getItemElement('mmodMarker')
    if(elMarker) {
      elList.style.paddingLeft = 'var(--base_height)'
    }else {
      elList.style.paddingLeft = ''
    }
  } // hideHiddenModules

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * change of application mode
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} params.mode - mode to be set
  */
  mmMode(sender, params) {
    const { mode } = params
    if(this.mode===mode) return
    this.mode = mode
    if(mode === 'admin' && this.config.admin_modules.length>0) {
      this._elAppBar.items = this.config.admin_modules
    }else {
      this._elAppBar.items = this.config.modules
      this.el.removeAttribute('admin_modules')
    }

    this.el.forAll('mele-item[key=theme]', el=>{
      el.label = 'theme.'+this.theme
      el.iconleft = 'sys.theme.'+this.theme
    })
    this.hideHiddenModules(this._elAppBar)
  } // mmMode

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Meta information about Doppler radar stream
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {integer} params.channel - camera channel
  * @param {string} params.state
  * @param {array} [params.errors]
  * @param {object} [params.sog] - Doppler speed over ground
  * @param {object} [params.gps] - Doppler speed over ground
  * @param {object} [params.identity] - Doppler device identification
  * @param {object} [params.calibration] - Doppler calibration data
  * @param {object} [params.optics] - Current Doppler optics setting
  * @param {object} [params.limits] - Doppler measurement limits
  * @param {object} [params.stats] - Doppler statistics
  * @param {integer} [params.power] - power of radar sensor
  * @param {string} [params.integration] - state of integration
  * @param {integer} [params.admin] - if true, serial unlock device is inserted and calibration can be performed
  */
  mmDopplerMeta(sender, params) {
    const { state, stats } = params

    if(this.indicatorDopplerReset) {
      clearTimeout(this.indicatorDopplerReset)
    }
    this.indicatorDopplerReset = setTimeout(()=>{
      this.elDoppler.value = 0
    }, 2000)

    let doppler = 0
    if(state === 'on') {
      doppler = 1
    }else if(state === 'off' || state === 'poweroff') {
      doppler = 2
    }else {
      doppler = 0
    }
    this.elDoppler.value = doppler

    const gps_count = stats?.gps_count || 0
    if(gps_count > this.last_doppler_gps_count) {
      this.last_gps_update = Date.now()
    }else {
      this.last_gps_update = Date.now() - this.config.gps_valid_time - 1000 // force GPS to be off
      this.elGPS.value = 0
    }
    this.last_doppler_gps_count = gps_count
  } // mmDopplerMeta

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
   * @description
   * Meta information of the listener
   * @param {object} sender - message sender
   * @param {object} params - message parameters
   * @param {string} [params.state]
   * @param {object} [params.stats]
   * @param {array} [params.errors]
   */
  mmMeta(sender, params) {
    const elItem = this._elAppBar.getItemElement(sender.listener_id)
    if (elItem) {
      const state = params.state ?? ''
      if (state === '') {
        elItem.removeAttribute('state')
      } else {
        elItem.setAttribute('state', state)
      }
    }
    // Generic notification handler for all module types
    if (params?.stats?.type) {
      this.handleModuleNotification(sender.listener_id, params.stats)
    }
  } // mmMeta

  /**
   * Generic notification handler for any module
   * @param {string} moduleKey - the module key (e.g., 'mmodIncidents', 'mmodInstructions')
   * @param {object} stats - notification stats with type, priority, label, and optional clear flag
   */
  handleModuleNotification(moduleKey, stats) {
    if (stats.clear) {
      this.removeModuleNotification(moduleKey)
      return
    }
    this.el.forAll(`mele-item[key=${moduleKey}]`, el => {
      const isOpen = this._elAppBar.selected?.includes(moduleKey)
      const currentPriority = parseInt(el.getAttribute('priority')) || 0

      // Update label if provided (e.g., ALPR shows license plate)
      if (stats.label) {
        el.label = stats.label
      }

      if (!isOpen && stats.priority && stats.priority > currentPriority) {
        this.removeModuleNotification(moduleKey)
        el.classList.add('moduleNotification')
        el.setAttribute('priority', stats.priority.toString())
      }
    })
  }

  /**
   * Remove notification from a specific module
   * @param {string} moduleKey - the module key
   */
  removeModuleNotification(moduleKey) {
    this.el.forAll(`mele-item[key=${moduleKey}]`, el => {
      el.classList.remove('moduleNotification')
      el.removeAttribute('priority')
    })
  }
} // mmodAppBar
